import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import {
    Paper,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Avatar,
    CircularProgress,
    TablePagination,
    Box,
    Card,
    CardContent,
    Grid,
    Chip,
    Skeleton,
    Fade,
    Container,
    Stack,
    Divider,
    Badge,
    IconButton,
    Tooltip,
    Button,
    Input,
    InputAdornment,
} from "@mui/material";
import { contestServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { IMAGE_BASE } from "../../utils/constants";
import { capitalizeFirstLetter } from "../../utils/helpers";
import CardLayout from "../../layouts/CardLayout";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import DiamondIcon from "@mui/icons-material/Diamond";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleIcon from "@mui/icons-material/People";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

// Helper function to get rank icon and color
const getRankIcon = (rank) => {
    switch (rank) {
        case 1:
            return { icon: <DiamondIcon />, color: "#FFD700", bgColor: "rgba(255, 215, 0, 0.1)" };
        case 2:
            return { icon: <LocalFireDepartmentIcon />, color: "#C0C0C0", bgColor: "rgba(192, 192, 192, 0.1)" };
        case 3:
            return { icon: <WorkspacePremiumIcon />, color: "#CD7F32", bgColor: "rgba(205, 127, 50, 0.1)" };
        default:
            return { icon: <StarBorderIcon />, color: "#637381", bgColor: "rgba(99, 115, 129, 0.1)" };
    }
};

// Loading skeleton component
const LeaderboardSkeleton = () => (
    <Box>
        <Grid container spacing={3} sx={{ mb: 3 }}>
            {[1, 2, 3].map((item) => (
                <Grid item xs={12} md={4} key={item}>
                    <Card sx={{ p: 2, textAlign: 'center' }}>
                        <Skeleton variant="circular" width={60} height={60} sx={{ mx: 'auto', mb: 2 }} />
                        <Skeleton variant="text" width="60%" sx={{ mx: 'auto' }} />
                        <Skeleton variant="text" width="40%" sx={{ mx: 'auto' }} />
                    </Card>
                </Grid>
            ))}
        </Grid>
        <Card>
            <CardContent>
                <Skeleton variant="rectangular" height={400} />
            </CardContent>
        </Card>
    </Box>
);

// Podium component for top 3
const PodiumCard = ({ user, rank, index, onPlayerClick }) => {
    const rankInfo = getRankIcon(rank);
    const height = rank === 1 ? 120 : rank === 2 ? 100 : 80;
    
    return (
        <Fade in timeout={500 + index * 200}>
            <Card 
                sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    background: `linear-gradient(135deg, ${rankInfo.bgColor} 0%, rgba(0,0,0,0.1) 100%)`,
                    border: `2px solid ${rankInfo.color}`,
                    position: 'relative',
                    overflow: 'visible',
                    minHeight: height,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 25px ${rankInfo.color}40`,
                        transition: 'all 0.3s ease'
                    }
                }}
            >
                <Box sx={{ position: 'absolute', top: -15, left: '50%', transform: 'translateX(-50%)' }}>
                    <Chip
                        icon={rankInfo.icon}
                        label={`#${rank}`}
                        sx={{
                            bgcolor: rankInfo.color,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.9rem'
                        }}
                    />
                </Box>
                
                <Avatar
                    src={`${IMAGE_BASE}${user.image}`}
                    alt={user?.username}
                    sx={{ 
                        width: 60, 
                        height: 60, 
                        mx: 'auto', 
                        mb: 1,
                        border: `3px solid ${rankInfo.color}`,
                        boxShadow: `0 0 20px ${rankInfo.color}50`
                    }}
                />
                
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {capitalizeFirstLetter(user.username)}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {user.firstname} {user.lastname}
                </Typography>
                
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2 }}>
                    <Box textAlign="center">
                        <Typography variant="caption" color="text.secondary">
                            Game Points
                        </Typography>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                            {user.gamepoints || 0}
                        </Typography>
                    </Box>
                    <Box textAlign="center">
                        <Typography variant="caption" color="text.secondary">
                            Overall
                        </Typography>
                        <Typography variant="h6" color="secondary" sx={{ fontWeight: 'bold' }}>
                            {user.overallpoints || 0}
                        </Typography>
                    </Box>
                </Stack>

                {user.teamId ? (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => onPlayerClick && onPlayerClick(user)}
                        sx={{
                            minWidth: 120,
                            height: 32,
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            bgcolor: rankInfo.color,
                            color: 'white',
                            '&:hover': {
                                bgcolor: rankInfo.color,
                                opacity: 0.9,
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${rankInfo.color}60`
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        Team Preview
                    </Button>
                ) : (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SportsSoccerIcon />}
                        disabled
                        sx={{
                            minWidth: 120,
                            height: 32,
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            borderColor: rankInfo.color,
                            color: rankInfo.color,
                        }}
                    >
                        No Team
                    </Button>
                )}
            </Card>
        </Fade>
    );
};

const LeaderboardScreen = () => {
    const { id } = useParams(); // Contest ID from URL
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [leaderboard, setLeaderboard] = useState([]);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [contestInfo, setContestInfo] = useState(null);
    const [searchItem, setSearchItem] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Debounced search function
    const debouncedSearchFunction = useCallback(
        debounce((query) => {
            setDebouncedSearch(query);
        }, 400),
        []
    );

    const handleSearchChange = (event) => {
        const query = event.target.value;
        setSearchItem(query);
        setPage(1); // Reset to first page when searching
        debouncedSearchFunction(query);
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            setDebouncedSearch(searchItem);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchItem]);

    useEffect(() => {
        fetchLeaderboard();
        fetchContestInfo();
    }, [id, page, rowsPerPage, debouncedSearch]);

    const fetchLeaderboard = async () => {
        try {
            setLoading(true);
            const response = await contestServices.getLeaderboard({
                contestId: id,
                pageNumber: page,
                pageSize: rowsPerPage,
                searchItem: debouncedSearch || "",
            });

            setLeaderboard(response?.data?.data?.data || []);
            setTotalCount(response?.data?.data?.totalCount || 0);
        } catch (error) {
            errorHandler(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchContestInfo = async () => {
        try {
            // This would typically fetch contest details
            // For now, we'll use mock data
            setContestInfo({
                name: "Weekly Championship",
                participants: totalCount,
                prize: "$10,000",
                status: "Active"
            });
        } catch (error) {
            console.error("Error fetching contest info:", error);
        }
    };

    const handlePlayerClick = (player) => {
        // Navigate to team preview page with player's team ID
        if (player.teamId) {
            navigate(`/contest-management/team-preview/${player.teamId}`);
        } else {
            console.warn("No team ID found for player:", player);
        }
    };

    // Get top 3 for podium display
    const topThree = leaderboard.slice(0, 3);
    const remainingUsers = leaderboard.slice(3);

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <CardLayout>
                {/* Header Section */}
                <Box sx={{ mb: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                        <EmojiEventsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                Contest Leaderboard
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {contestInfo?.name || "Weekly Championship"}
                            </Typography>
                        </Box>
                    </Stack>

                    {/* Contest Stats */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
                                <PeopleIcon sx={{ fontSize: 30, mb: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    {totalCount}
                                </Typography>
                                <Typography variant="body2">
                                    Total Participants
                                </Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.main', color: 'white' }}>
                                <EmojiEventsIcon sx={{ fontSize: 30, mb: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    {contestInfo?.status || "Active"}
                                </Typography>
                                <Typography variant="body2">
                                    Status
                </Typography>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>

                {/* Search Section */}
                <Box sx={{ mb: 3 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            background: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <SearchIcon sx={{ color: 'text.secondary' }} />
                            <Input
                                value={searchItem}
                                placeholder="Search participants by name, team, or rank..."
                                onChange={handleSearchChange}
                                sx={{
                                    flex: 1,
                                    '& .MuiInput-input': {
                                        color: 'text.primary',
                                        fontSize: '16px',
                                    },
                                    '& .MuiInput-input::placeholder': {
                                        color: 'text.secondary',
                                        opacity: 1,
                                    },
                                    '& .MuiInput-root': {
                                        backgroundColor: 'transparent',
                                        '&:before': {
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                        },
                                        '&:hover:not(.Mui-disabled):before': {
                                            borderBottom: '2px solid',
                                            borderColor: 'primary.main',
                                        },
                                        '&.Mui-focused:after': {
                                            borderBottom: '2px solid',
                                            borderColor: 'primary.main',
                                        }
                                    }
                                }}
                                endAdornment={
                                    searchItem && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="clear-search"
                                                onClick={() => {
                                                    setSearchItem("");
                                                    setDebouncedSearch("");
                                                    setPage(1);
                                                }}
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }
                            />
                        </Stack>
                    </Paper>
                </Box>

                {loading ? (
                    <LeaderboardSkeleton />
                ) : leaderboard.length === 0 ? (
                    <Fade in>
                        <Card sx={{ p: 6, textAlign: 'center' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '300px',
                                    opacity: 0.7
                                }}
                            >
                                <EmojiEventsIcon sx={{ fontSize: 80, mb: 2, color: 'text.disabled' }} />
                                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    {debouncedSearch ? 'No Search Results' : 'No Leaderboard Data'}
                        </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    {debouncedSearch 
                                        ? `No participants found matching "${debouncedSearch}". Try a different search term.`
                                        : 'Once users start participating in this contest, their rankings will appear here.'
                                    }
                        </Typography>
                                <Chip
                                    label={debouncedSearch ? "Try different search terms" : "Contest is ready for participants"}
                                    color="primary"
                                    variant="outlined"
                                    icon={<PeopleIcon />}
                                />
                    </Box>
                        </Card>
                    </Fade>
                ) : (
                    <Fade in>
                        <Box>
                            {/* Podium for Top 3 - Commented Out */}
                            {/* {topThree.length > 0 && (
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center' }}>
                                        🏆 Top Performers
                                    </Typography>
                                    <Grid container spacing={2} justifyContent="center">
                                        {topThree.map((user, index) => (
                                            <Grid item xs={12} sm={6} md={4} key={user.rank || index}>
                                                <PodiumCard 
                                                    user={user} 
                                                    rank={user.rank || index + 1} 
                                                    index={index}
                                                    onPlayerClick={handlePlayerClick}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )} */}

                            {/* Complete Rankings - Enhanced Design */}
                            {leaderboard.length > 0 && (
                                <Card sx={{ 
                                    overflow: 'hidden', 
                                    mt: 3,
                                    background: 'linear-gradient(135deg, rgba(0, 161, 221, 0.02) 0%, rgba(217, 83, 35, 0.02) 100%)',
                                    border: '1px solid rgba(0, 161, 221, 0.1)',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                                }}>
                                    {/* Enhanced Table Header */}
                                    <Box sx={{ 
                                        p: 4, 
                                        background: 'linear-gradient(135deg, #00a1dd 0%, #D95323 100%)',
                                        color: 'white',
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}>
                                        <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.1 }}>
                                            <EmojiEventsIcon sx={{ fontSize: 120, transform: 'rotate(15deg)' }} />
                                        </Box>
                                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <EmojiEventsIcon sx={{ fontSize: 28 }} />
                                                Complete Rankings
                                            </Typography>
                                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                                Full leaderboard with all participants and performance metrics
                                            </Typography>
                                        </Box>
                                    </Box>
                                    
                                    {/* Enhanced Table */}
                        <Table>
                            <TableHead>
                                            <TableRow sx={{ 
                                                bgcolor: 'background.neutral',
                                                '& .MuiTableCell-head': {
                                                    borderBottom: '2px solid',
                                                    borderColor: 'primary.main',
                                                    fontWeight: 'bold',
                                                    fontSize: '0.95rem',
                                                    py: 3,
                                                    color: 'text.primary'
                                                }
                                            }}>
                                                <TableCell sx={{ pl: 4 }}>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        {/* <MilitaryMedalIcon sx={{ fontSize: 20, color: 'primary.main' }} /> */}
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                            Rank
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <PeopleIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                            Player
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center' }}>
                                                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                                        <TrendingUpIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                            Game Points
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center' }}>
                                                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                                        <StarIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                            Overall Points
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center', pr: 4 }}>
                                                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                                        <WorkspacePremiumIcon sx={{ fontSize: 20, color: 'success.main' }} />
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                            Performance
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                            {leaderboard.map((user, index) => {
                                                const rank = user.rank || index + 1;
                                                const rankInfo = getRankIcon(rank);
                                                const isTopThree = rank <= 3;
                                                const isTopTen = rank <= 10;
                                                
                                                return (
                                                    <TableRow 
                                                        key={index}
                                                        sx={{
                                                            '&:hover': {
                                                                bgcolor: 'action.hover',
                                                                transform: 'scale(1.01)',
                                                                transition: 'all 0.2s ease',
                                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                                            },
                                                            '&:nth-of-type(odd)': {
                                                                bgcolor: 'background.paper'
                                                            },
                                                            '&:nth-of-type(even)': {
                                                                bgcolor: 'rgba(0, 161, 221, 0.02)'
                                                            },
                                                            borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
                                                        }}
                                                    >
                                                        {/* Enhanced Rank Column */}
                                                        <TableCell sx={{ py: 3, pl: 4 }}>
                                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                                <Box sx={{
                                                                    width: 40,
                                                                    height: 40,
                                                                    borderRadius: '50%',
                                                                    background: isTopThree 
                                                                        ? `linear-gradient(135deg, ${rankInfo.color} 0%, ${rankInfo.color}dd 100%)`
                                                                        : isTopTen 
                                                                        ? 'linear-gradient(135deg, #00a1dd 0%, #00a1dddd 100%)'
                                                                        : 'linear-gradient(135deg, #637381 0%, #637381dd 100%)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: 'white',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '1rem',
                                                                    boxShadow: isTopThree 
                                                                        ? `0 4px 15px ${rankInfo.color}40`
                                                                        : '0 4px 15px rgba(0, 0, 0, 0.2)',
                                                                    border: '2px solid white'
                                                                }}>
                                                                    {rank}
                                                                </Box>
                                                                {isTopThree && (
                                                                    <Tooltip title={`Top ${rank} Player`} arrow>
                                                                        <Box sx={{ 
                                                                            p: 0.5, 
                                                                            borderRadius: '50%', 
                                                                            bgcolor: `${rankInfo.color}20`,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center'
                                                                        }}>
                                                                            <StarIcon sx={{ color: rankInfo.color, fontSize: 20 }} />
                                                                        </Box>
                                                                    </Tooltip>
                                                                )}
                                                            </Stack>
                                                        </TableCell>
                                                        
                                                        {/* Enhanced Player Column */}
                                                        <TableCell sx={{ py: 3 }}>
                                                            <Stack direction="row" alignItems="center" spacing={3}>
                                                                <Box sx={{ position: 'relative' }}>
                                            <Avatar
                                                src={`${IMAGE_BASE}${user.image}`}
                                                alt={user?.username}
                                                                        sx={{ 
                                                                            width: 55, 
                                                                            height: 55,
                                                                            border: isTopThree ? `3px solid ${rankInfo.color}` : isTopTen ? '2px solid #00a1dd' : '2px solid #e0e0e0',
                                                                            boxShadow: isTopThree 
                                                                                ? `0 0 20px ${rankInfo.color}40`
                                                                                : '0 4px 12px rgba(0, 0, 0, 0.15)'
                                                                        }}
                                                                    />
                                                                    {isTopThree && (
                                                                        <Box sx={{
                                                                            position: 'absolute',
                                                                            top: -5,
                                                                            right: -5,
                                                                            width: 20,
                                                                            height: 20,
                                                                            borderRadius: '50%',
                                                                            bgcolor: rankInfo.color,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            fontSize: '0.7rem',
                                                                            fontWeight: 'bold',
                                                                            color: 'white',
                                                                            border: '2px solid white'
                                                                        }}>
                                                                            {rank}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                                <Box>
                                                                    <Typography variant="h6" sx={{ 
                                                                        fontWeight: 'bold', 
                                                                        mb: 0.5,
                                                                        color: isTopThree ? rankInfo.color : 'text.primary',
                                                                        fontSize: '1.1rem'
                                                                    }}>
                                                                        {capitalizeFirstLetter(user.username)}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                                        {user.firstname} {user.lastname}
                                                                    </Typography>
                                                                    <Chip
                                                                        label={isTopThree ? "Champion" : isTopTen ? "Elite" : "Competitor"}
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: isTopThree ? rankInfo.color : isTopTen ? '#00a1dd' : '#637381',
                                                                            color: 'white',
                                                                            fontWeight: 'bold',
                                                                            fontSize: '0.75rem',
                                                                            height: 22
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Stack>
                                                        </TableCell>
                                                        
                                                        {/* Enhanced Game Points Column */}
                                                        <TableCell sx={{ textAlign: 'center', py: 3 }}>
                                                            <Box sx={{
                                                                display: 'inline-flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                p: 2,
                                                                borderRadius: 2,
                                                                bgcolor: 'rgba(0, 161, 221, 0.1)',
                                                                border: '1px solid rgba(0, 161, 221, 0.2)',
                                                                minWidth: 80
                                                            }}>
                                                                <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                                    {user.gamepoints || 0}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Points
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        
                                                        {/* Enhanced Overall Points Column */}
                                                        <TableCell sx={{ textAlign: 'center', py: 3 }}>
                                                            <Box sx={{
                                                                display: 'inline-flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                p: 2,
                                                                borderRadius: 2,
                                                                bgcolor: 'rgba(217, 83, 35, 0.1)',
                                                                border: '1px solid rgba(217, 83, 35, 0.2)',
                                                                minWidth: 80
                                                            }}>
                                                                <Typography variant="h5" color="secondary" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                                    {user.overallpoints || 0}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Overall
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        
                                                        {/* Enhanced Performance Column */}
                                                        <TableCell sx={{ textAlign: 'center', py: 3, pr: 4 }}>
                                                            <Stack direction="column" spacing={2} alignItems="center">
                                                                <Chip
                                                                    label={isTopThree ? "Champion" : isTopTen ? "Elite" : "Competitor"}
                                                                    color={isTopThree ? "success" : isTopTen ? "primary" : "default"}
                                                                    variant={isTopThree ? "filled" : "outlined"}
                                                                    sx={{ 
                                                                        fontWeight: 'bold',
                                                                        fontSize: '0.8rem',
                                                                        height: 28,
                                                                        px: 2
                                                                    }}
                                                                />
                                                                {user.teamId ? (
                                                                    <Tooltip title="Preview Team" arrow>
                                                                        <Button
                                                                            variant="outlined"
                                                                            size="small"
                                                                            startIcon={<VisibilityIcon />}
                                                                            onClick={() => handlePlayerClick(user)}
                                                                            sx={{
                                                                                minWidth: 100,
                                                                                height: 32,
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 'bold',
                                                                                borderColor: 'primary.main',
                                                                                color: 'primary.main',
                                                                                '&:hover': {
                                                                                    bgcolor: 'primary.main',
                                                                                    color: 'white',
                                                                                    transform: 'translateY(-1px)',
                                                                                    boxShadow: '0 4px 12px rgba(0, 161, 221, 0.3)'
                                                                                },
                                                                                transition: 'all 0.2s ease'
                                                                            }}
                                                                        >
                                                                            Team Preview
                                                                        </Button>
                                                                    </Tooltip>
                                                                ) : (
                                                                    <Tooltip title="No team data available" arrow>
                                                                        <Button
                                                                            variant="outlined"
                                                                            size="small"
                                                                            startIcon={<SportsSoccerIcon />}
                                                                            disabled
                                                                            sx={{
                                                                                minWidth: 100,
                                                                                height: 32,
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 'bold',
                                                                            }}
                                                                        >
                                                                            No Team
                                                                        </Button>
                                                                    </Tooltip>
                                                                )}
                                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                                );
                                            })}
                            </TableBody>
                        </Table>

                                    {/* Enhanced Pagination */}
                                    <Divider />
                                    <Box sx={{ 
                                        p: 3, 
                                        bgcolor: 'background.neutral',
                                        borderTop: '1px solid rgba(0, 161, 221, 0.1)'
                                    }}>
                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={page - 1}
                            onPageChange={(e, newPage) => setPage(newPage + 1)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(1);
                            }}
                            rowsPerPageOptions={[5, 10, 20, 50]}
                                            sx={{
                                                '& .MuiTablePagination-toolbar': {
                                                    px: 0
                                                },
                                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                                    fontWeight: 'bold'
                                                }
                                            }}
                                        />
                                    </Box>
                                </Card>
                            )}
                        </Box>
                    </Fade>
                )}
            </CardLayout>
        </Container>
    );
};

export default LeaderboardScreen;
